import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Activity, Wallet, CalendarDays, FileText, ShieldCheck, Settings, BadgeCheck,
  GraduationCap, Plus, CheckCircle2, AlertTriangle, Trash2, Award,
  Calendar, Loader2, Search, Stethoscope, ListChecks, Building2, Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CERT_KIND_LABEL = {
  rn_license: 'RN License', lpn_license: 'LPN License', np_license: 'NP License',
  physician_license: 'Physician License', pt_license: 'PT License',
  ot_license: 'OT License', slp_license: 'SLP License',
  cna_certification: 'CNA', hha_certification: 'HHA',
  bls: 'BLS', acls: 'ACLS', cpr: 'CPR',
  wound_care: 'Wound Care', lactation_consultant: 'Lactation',
  case_management: 'Case Management', malpractice_insurance: 'Malpractice Insurance',
  background_check: 'Background Check', tb_screening: 'TB Screening',
  covid_vaccine: 'COVID Vaccine', other: 'Other',
};
const CERT_KINDS = Object.keys(CERT_KIND_LABEL);

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / 86400000);
};

const CertStatusPill = ({ expiresAt }) => {
  const d = daysUntil(expiresAt);
  if (d === null) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-cn-bone-100 text-cn-text-muted border border-cn-border">No expiry</span>;
  if (d < 0) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold">Expired {Math.abs(d)}d ago</span>;
  if (d <= 14) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">Expiring in {d}d</span>;
  if (d <= 90) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-cn-bone-100 text-cn-text border border-cn-border">{d}d left</span>;
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>;
};

export default function ClinicianCeu() {
  const { getAuthHeader } = useAuth();
  const [tab, setTab] = useState('library');
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [certs, setCerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c1, c2, c3] = await Promise.all([
        axios.get(`${API}/ceu/courses`, getAuthHeader()),
        axios.get(`${API}/clinician/certifications`, getAuthHeader()),
        axios.get(`${API}/clinician/ceu/summary`, getAuthHeader()),
      ]);
      setCourses(c1.data?.courses || []);
      setCerts(c2.data?.certifications || []);
      setSummary(c3.data || null);
    } catch (e) {
      toast.error('Could not load CEU dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const filteredCourses = useMemo(() => {
    if (!search.trim()) return courses;
    const s = search.toLowerCase();
    return courses.filter((c) =>
      (c.title || '').toLowerCase().includes(s) ||
      (c.description || '').toLowerCase().includes(s) ||
      (c.discipline || '').toLowerCase().includes(s),
    );
  }, [courses, search]);

  const completedHours = summary?.total_hours || 0;
  const completedCount = (summary?.completions || []).length;

  const expiringSoon = useMemo(() =>
    certs.filter((c) => {
      const d = daysUntil(c.expires_at);
      return d !== null && d >= 0 && d <= 30;
    }), [certs]);
  const lapsed = useMemo(() =>
    certs.filter((c) => {
      const d = daysUntil(c.expires_at);
      return d !== null && d < 0;
    }), [certs]);

  const complete = async (course) => {
    setCompleting(course.id);
    try {
      const { data } = await axios.post(`${API}/clinician/ceu/complete/${course.id}`, {}, getAuthHeader());
      if (data.already_completed) toast.info(`You already completed ${course.title}`);
      else toast.success(`+${course.ceu_hours} CEU hours — ${course.title}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete course');
    } finally {
      setCompleting(null);
    }
  };

  const removeCert = async (cert) => {
    if (!window.confirm(`Archive ${cert.name}?`)) return;
    try {
      await axios.delete(`${API}/clinician/certifications/${cert.id}`, getAuthHeader());
      toast.success('Certification archived');
      await load();
    } catch (e) { toast.error('Could not archive'); }
  };

  const navItems = [
    { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard' },
    { icon: CalendarDays, label: 'Schedule', href: '/clinician/schedule' },
    { icon: Stethoscope, label: 'Clinical Notes', href: '/clinician/notes' },
    { icon: FileText, label: 'Billing & RCM', href: '/clinician/billing' },
    { icon: Wallet, label: 'Banking', href: '/clinician/banking' },
    { icon: Megaphone, label: 'Open Shifts', href: '/workforce/shifts' },
    { icon: BadgeCheck, label: 'CEU & Certifications', active: true },
    { icon: ShieldCheck, label: 'Privacy & Data', href: '/privacy/dashboard' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="CEU & Certifications"
      subtitle="Continuing-education hours, license expiries, and renewal reminders — all in one place."
      testId="clinician-ceu-page"
    >
      {/* HEADLINE STRIP */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="cn-card p-5" data-testid="ceu-summary-hours">
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">CEU hours (lifetime)</p>
          <p className="font-display font-bold text-3xl text-cn-text dark:text-white mt-1">{completedHours.toFixed(1)}</p>
          <p className="text-xs text-cn-text-muted mt-1">{completedCount} course{completedCount === 1 ? '' : 's'} completed</p>
        </div>
        <div className="cn-card p-5" data-testid="ceu-summary-expiring">
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Expiring within 30 days</p>
          <p className="font-display font-bold text-3xl text-cn-text dark:text-white mt-1">{expiringSoon.length}</p>
          <p className="text-xs text-cn-text-muted mt-1">{lapsed.length} already lapsed</p>
        </div>
        <div className="cn-card p-5" data-testid="ceu-summary-certs">
          <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Active credentials</p>
          <p className="font-display font-bold text-3xl text-cn-text dark:text-white mt-1">{certs.length}</p>
          <p className="text-xs text-cn-text-muted mt-1">Across {Object.keys(summary?.by_state || {}).length} state{Object.keys(summary?.by_state || {}).length === 1 ? '' : 's'}</p>
        </div>
      </div>

      {/* LAPSED BANNER */}
      {lapsed.length > 0 && (
        <div className="cn-card p-5 mb-6 flex items-start gap-3 bg-red-50/60 border-red-200" data-testid="ceu-lapsed-banner">
          <AlertTriangle className="w-5 h-5 mt-0.5 text-red-600 shrink-0" />
          <div>
            <h4 className="font-semibold text-cn-text mb-1">{lapsed.length} credential{lapsed.length === 1 ? '' : 's'} lapsed</h4>
            <p className="text-sm text-cn-text-muted">Re-verify in this dashboard to keep getting new placements. Lapsed credentials auto-revoke new assignments.</p>
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="flex gap-2 mb-5 border-b border-cn-border" data-testid="ceu-tabs">
        <button
          onClick={() => setTab('library')}
          className={`px-4 py-2 text-sm font-semibold transition-colors -mb-px border-b-2 ${tab === 'library' ? 'border-cn-primary text-cn-primary' : 'border-transparent text-cn-text-muted hover:text-cn-text'}`}
          data-testid="ceu-tab-library"
        >
          <GraduationCap className="w-4 h-4 inline -mt-0.5 mr-1.5" />
          CEU Library
        </button>
        <button
          onClick={() => setTab('certs')}
          className={`px-4 py-2 text-sm font-semibold transition-colors -mb-px border-b-2 ${tab === 'certs' ? 'border-cn-primary text-cn-primary' : 'border-transparent text-cn-text-muted hover:text-cn-text'}`}
          data-testid="ceu-tab-certs"
        >
          <ListChecks className="w-4 h-4 inline -mt-0.5 mr-1.5" />
          Certifications ({certs.length})
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-2 text-sm font-semibold transition-colors -mb-px border-b-2 ${tab === 'history' ? 'border-cn-primary text-cn-primary' : 'border-transparent text-cn-text-muted hover:text-cn-text'}`}
          data-testid="ceu-tab-history"
        >
          <Award className="w-4 h-4 inline -mt-0.5 mr-1.5" />
          My Hours
        </button>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && tab === 'library' && (
        <div data-testid="ceu-library-panel">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cn-text-muted" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search courses by title, description, or discipline"
                className="pl-9"
                data-testid="ceu-library-search"
              />
            </div>
            <span className="text-xs text-cn-text-muted">{filteredCourses.length} course{filteredCourses.length === 1 ? '' : 's'}</span>
          </div>

          {filteredCourses.length === 0 ? (
            <div className="cn-card p-12 text-center" data-testid="ceu-library-empty">
              <GraduationCap className="w-10 h-10 text-cn-text-muted mx-auto mb-2" />
              <p className="text-sm text-cn-text-muted">No courses match your search. CaraLoom Academy is curating new state-board-aligned content monthly.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filteredCourses.map((c) => (
                <div key={c.id} className="cn-card p-5 flex flex-col" data-testid={`ceu-course-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-display font-semibold text-base text-cn-text dark:text-white">{c.title}</h3>
                      <p className="text-xs text-cn-text-muted mt-0.5">{c.provider} · {c.duration_minutes} min</p>
                    </div>
                    <span className="text-[11px] px-2.5 py-1 rounded-full bg-cn-primary/10 text-cn-primary font-bold">{c.ceu_hours} CEU</span>
                  </div>
                  <p className="text-sm text-cn-text-muted leading-relaxed mb-3 line-clamp-3">{c.description}</p>
                  {(c.accredited_states || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(c.accredited_states || []).slice(0, 6).map((s) => (
                        <span key={s} className="text-[10px] px-1.5 py-0.5 bg-cn-bone-100 border border-cn-border rounded text-cn-text-muted">{s}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto pt-2 flex items-center gap-2">
                    {c.completed ? (
                      <span className="text-sm font-semibold text-emerald-600 inline-flex items-center gap-1" data-testid={`ceu-course-completed-${c.id}`}>
                        <CheckCircle2 className="w-4 h-4" /> Completed
                      </span>
                    ) : (
                      <Button
                        onClick={() => complete(c)}
                        disabled={completing === c.id}
                        className="cn-btn cn-btn-primary text-xs !py-1.5"
                        data-testid={`ceu-course-complete-${c.id}`}
                      >
                        {completing === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark complete'}
                      </Button>
                    )}
                    {c.external_url && (
                      <a href={c.external_url} target="_blank" rel="noreferrer" className="text-xs text-cn-primary font-semibold hover:underline">
                        Open course →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && tab === 'certs' && (
        <div data-testid="ceu-certs-panel">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-cn-text-muted">{certs.length} active credential{certs.length === 1 ? '' : 's'}. Renewal reminders fire 14 days before expiry.</p>
            <Button onClick={() => setShowAdd(true)} className="cn-btn cn-btn-primary !py-2" data-testid="ceu-cert-add-btn">
              <Plus className="w-4 h-4" /> Add credential
            </Button>
          </div>

          {certs.length === 0 ? (
            <div className="cn-card p-12 text-center" data-testid="ceu-certs-empty">
              <BadgeCheck className="w-10 h-10 text-cn-text-muted mx-auto mb-2" />
              <p className="text-sm text-cn-text-muted">No credentials logged yet. Add your active licenses + certifications so we can route work + remind you before they expire.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="ceu-certs-list">
              {certs.map((c) => (
                <div key={c.id} className="cn-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`ceu-cert-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center"><BadgeCheck className="w-5 h-5" /></span>
                    <div>
                      <p className="font-semibold text-cn-text">{c.name}</p>
                      <p className="text-xs text-cn-text-muted">{CERT_KIND_LABEL[c.kind] || c.kind}{c.state ? ` · ${c.state}` : ''}{c.number ? ` · ${c.number}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {c.expires_at && (
                      <span className="text-xs text-cn-text-muted inline-flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(c.expires_at).toLocaleDateString()}
                      </span>
                    )}
                    <CertStatusPill expiresAt={c.expires_at} />
                    <button onClick={() => removeCert(c)} className="p-1.5 hover:bg-cn-bone-100 rounded text-cn-text-muted hover:text-red-600" data-testid={`ceu-cert-remove-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAdd && <AddCertModal onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await load(); }} />}
        </div>
      )}

      {!loading && tab === 'history' && (
        <div data-testid="ceu-history-panel">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="cn-card p-5">
              <h4 className="font-semibold text-cn-text mb-3">Hours by year</h4>
              {Object.keys(summary?.by_year || {}).length === 0 ? (
                <p className="text-sm text-cn-text-muted">No completions yet.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(summary.by_year).sort(([a], [b]) => b.localeCompare(a)).map(([y, h]) => (
                    <div key={y} className="flex items-center justify-between text-sm">
                      <span className="text-cn-text">{y}</span>
                      <span className="font-bold text-cn-primary">{h} hrs</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="cn-card p-5">
              <h4 className="font-semibold text-cn-text mb-3">Hours by accreditation state</h4>
              {Object.keys(summary?.by_state || {}).length === 0 ? (
                <p className="text-sm text-cn-text-muted">Completed courses with no accredited-state coverage don&apos;t appear here.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(summary.by_state).sort(([, a], [, b]) => b - a).map(([s, h]) => (
                    <div key={s} className="flex items-center justify-between text-sm">
                      <span className="text-cn-text">{s}</span>
                      <span className="font-bold text-cn-primary">{h} hrs</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="cn-card p-5">
            <h4 className="font-semibold text-cn-text mb-3">Recent completions</h4>
            {(summary?.completions || []).length === 0 ? (
              <p className="text-sm text-cn-text-muted">Complete a course in the library to start building your CEU record.</p>
            ) : (
              <div className="space-y-2" data-testid="ceu-completions-list">
                {(summary.completions || []).slice(0, 20).map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-cn-border last:border-0 text-sm">
                    <div>
                      <p className="font-medium text-cn-text">{c.course_title}</p>
                      <p className="text-xs text-cn-text-muted">{new Date(c.completed_at).toLocaleDateString()} · {(c.accredited_states || []).join(', ') || 'No state accreditation'}</p>
                    </div>
                    <span className="text-sm font-bold text-cn-primary">{c.ceu_hours} hrs</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function AddCertModal({ onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({
    kind: 'rn_license', name: '', issuing_body: '', state: '', number: '',
    issued_at: '', expires_at: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || form.name.length < 2) {
      toast.error('Please name the credential');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.issued_at) delete payload.issued_at;
      if (!payload.expires_at) delete payload.expires_at;
      if (!payload.state) delete payload.state;
      if (!payload.number) delete payload.number;
      if (!payload.issuing_body) delete payload.issuing_body;
      await axios.post(`${API}/clinician/certifications`, payload, getAuthHeader());
      toast.success('Credential saved');
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ceu-cert-add-modal">
      <div className="cn-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-semibold text-lg text-cn-text mb-1">Add credential</h3>
        <p className="text-sm text-cn-text-muted mb-4">Active licenses, certifications, malpractice insurance, screening — anything with an expiry.</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">Credential type</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full border border-cn-border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="ceu-cert-form-kind">
              {CERT_KINDS.map((k) => <option key={k} value={k}>{CERT_KIND_LABEL[k]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">Display name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="RN License — Texas" data-testid="ceu-cert-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-cn-text-muted">State</label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="TX" maxLength={2} data-testid="ceu-cert-form-state" />
            </div>
            <div>
              <label className="text-xs font-semibold text-cn-text-muted">License #</label>
              <Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="123456" data-testid="ceu-cert-form-number" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-cn-text-muted">Issued</label>
              <Input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} data-testid="ceu-cert-form-issued" />
            </div>
            <div>
              <label className="text-xs font-semibold text-cn-text-muted">Expires</label>
              <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="ceu-cert-form-expires" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-cn-text-muted">Issuing body (optional)</label>
            <Input value={form.issuing_body} onChange={(e) => setForm({ ...form, issuing_body: e.target.value })} placeholder="Texas Board of Nursing" data-testid="ceu-cert-form-issuer" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <Button onClick={onClose} className="cn-btn cn-btn-ghost" data-testid="ceu-cert-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="cn-btn cn-btn-primary" data-testid="ceu-cert-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save credential'}
          </Button>
        </div>
      </div>
    </div>
  );
}
